import React, { useEffect, useMemo, useRef, useState } from "react";
import { StyleSheet, TouchableOpacity, View, FlatList } from "react-native";
import ArrowRightMedium from "@assets/svg/ArrowRightMedium";
import Cross from "@assets/svg/Cross";
import Tick from "@assets/svg/Tick";
import Text from "@components/CustomText";
import Image from "@components/common/Image";
import { width } from "@constants/metrics";
import {
  answerEarlyJourneyQuestionaire,
  getEarlyJourneyQuestionaire,
} from "@services/api";
import { actions, actions as configActions } from "@reducers/configReducer";
import analytics from "@services/analytics";
import { EARLY_JOURNEY } from "@constants/analyticsEventsCT";
import CircularProgress from "./CircularProgress";
import { useDispatch } from "react-redux";

const questionWidth = width - 30;

const OnBoardingQuestionaire = ({ navigation }) => {
  const [questionaire, setQuestionaire] = useState([]);
  const [selectedAnswer, setSelectedAnswer] = useState();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [percentage, setPercentage] = useState(0);
  const viewPageRef = useRef(null);
  const dispatch = useDispatch();
  useEffect(() => {
    getEarlyJourneyQuestionaire().then((response) => {
      const questions = response.data;
      if (Array.isArray(questions) && questions.length === 0) {
        navigation.replace("EnterShopName");
      } else {
        const currentQuestionPercentage = questions[0]?.initialPercentage;
        setQuestionaire(questions);
        setPercentage(currentQuestionPercentage);
      }
    });
  }, []);

  const handleOnPressFooterCta = (question) => {
    const newIndex = currentIndex + 1;
    analytics.trackEvent(EARLY_JOURNEY.ANSWER_EXPLANATION_MODAL_CLICKED, {
      answer_type: selectedAnswer.is_right
        ? "True"
        : selectedAnswer.is_wrong
          ? "False"
          : "False",
      option_selected: selectedAnswer.text,
      question: question.text,
      question_sequence:
        question.numberText && question.numberText.replace("$", ""),
    });

    if (newIndex === questionaire.length) {
      // navigation.replace("EnterShopName");
      dispatch(configActions.syncUser());
    } else {
      setCurrentIndex(newIndex);
      viewPageRef && viewPageRef.current.scrollToIndex({ index: newIndex });
      setSelectedAnswer(null);
    }
  };

  const handleOnPressAnswer = (key, afterClickPercentage, answer, question) => {
    setSelectedAnswer(answer);
    setPercentage(afterClickPercentage);
    answerEarlyJourneyQuestionaire({ key, value: answer.text });
    analytics.trackEvent(EARLY_JOURNEY.VIDEO_ANSWER_SUBMITTED, {
      answer_type: answer.is_right
        ? "True"
        : answer.is_wrong
          ? "False"
          : "False",
      option_selected: answer.text,
      question: question.text,
      question_sequence:
        question.numberText && question.numberText.replace("$", ""),
    });
  };

  const renderPerQuestion = ({ item }) => (
    <View style={styles.container}>
      <View style={styles.questionContainer}>
        <View style={styles.questionProgressContainer}>
          <View style={styles.progressBarLine} />
          <CircularProgress
            percentage={percentage}
            afterClickPercentage={item.afterClickPercentage}
          />
        </View>
        <Text newfont variant="body_text_2" style={styles.questionTitleText}>
          {item.question.numberText.slice(
            0,
            item.question.numberText.indexOf("$"),
          )}
          <Text
            newfont
            variant="body_text_2"
            style={{ color: "rgba(0, 171, 89, 1)" }}
          >
            {
              item.question.numberText[
                item.question.numberText.indexOf("$") + 1
              ]
            }
          </Text>
          {item.question.numberText.slice(
            item.question.numberText.indexOf("$") + 2,
          )}
        </Text>
        <Text newfont variant="title_20" style={styles.questionText}>
          {item.question.text}
        </Text>
      </View>
      <View style={styles.answerContainer}>
        <FlatList
          data={item.answer}
          keyExtractor={({ id }) => id}
          renderItem={({ item: answer }) => (
            <TouchableOpacity
              disabled={selectedAnswer}
              onPress={() =>
                handleOnPressAnswer(
                  item.key,
                  item.afterClickPercentage,
                  answer,
                  item.question,
                )
              }
              style={[
                styles.beforeAnswerStyleContainer,
                selectedAnswer?.id === answer.id &&
                  answer.is_right &&
                  styles.rightAnswerContainer,
                selectedAnswer?.id === answer.id &&
                  answer.is_wrong &&
                  styles.wrongAnswerContainer,
              ]}
            >
              {selectedAnswer?.id === answer.id && answer.is_right && (
                <View style={styles.rightTickContainer}>
                  <Tick width={18} strokeColor="#fff" />
                </View>
              )}
              {selectedAnswer?.id === answer.id && answer.is_wrong && (
                <View style={styles.wrongTickContainer}>
                  <Cross width={18} fill="#fff" />
                </View>
              )}
              {!(selectedAnswer?.id === answer.id) && (
                <View style={styles.emptyTickContainer} />
              )}
              <Text newfont variant="body_text_1" style={styles.answerText}>
                {answer.text}
              </Text>
            </TouchableOpacity>
          )}
        />
      </View>
      {selectedAnswer && (
        <View style={styles.footerContainer}>
          {Boolean(item.footer.image) && (
            <Image
              source={{
                uri: item.footer.image,
              }}
              style={styles.footerImage}
            />
          )}
          <Text newfont variant="title_18" style={styles.footerText}>
            {item.footer.text}
          </Text>
          <TouchableOpacity
            style={styles.nextQuestionCta}
            onPress={() => handleOnPressFooterCta(item.question)}
          >
            <Text
              newfont
              variant="subtitle_16"
              style={{ marginRight: 10, color: "rgba(0, 171, 89, 1)" }}
            >
              {item.footer.ctaText}
            </Text>
            <ArrowRightMedium fill="rgba(0, 171, 89, 1)" />
          </TouchableOpacity>
        </View>
      )}
    </View>
  );

  return (
    <FlatList
      ref={viewPageRef}
      horizontal
      pagingEnabled
      scrollEnabled={false}
      data={questionaire}
      keyExtractor={(obj) => obj.id}
      showsHorizontalScrollIndicator={false}
      renderItem={renderPerQuestion}
    />
  );
};

export default OnBoardingQuestionaire;

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#fff",
    // height: height - 100,
  },
  questionContainer: {
    borderWidth: 1,
    borderRadius: 30,
    marginTop: 50,
    padding: 20,
    borderColor: "rgba(0, 0, 0, 0.1)",
    backgroundColor: "#F7F7F7",
    marginHorizontal: 15,
    width: questionWidth,
  },
  questionTitleText: {
    color: "rgba(0, 0, 0, 0.56)",
    marginTop: 8,
  },
  questionProgressContainer: {
    marginLeft: -10,
  },
  questionText: {
    color: "rgba(0, 0, 0, 0.86)",
    marginTop: 10,
    marginRight: 32,
  },
  answerContainer: {
    marginTop: 15,
    marginHorizontal: 15,
    width: questionWidth,
  },
  beforeAnswerStyleContainer: {
    paddingVertical: 13,
    paddingHorizontal: 17,
    borderWidth: 1,
    borderColor: "rgba(0, 0, 0, 0.2)",
    borderRadius: 8,
    flexDirection: "row",
    marginBottom: 12,
    // alignItems: "center",
  },
  rightTickContainer: {
    height: 22,
    width: 22,
    borderWidth: 1,
    borderColor: "rgba(0,0,0,0.2)",
    backgroundColor: "rgba(0, 171, 89, 1)",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 22,
    padding: 5,
  },
  emptyTickContainer: {
    height: 22,
    width: 22,
    borderWidth: 3,
    borderColor: "rgba(0,0,0,0.2)",
    borderRadius: 22,
    padding: 5,
  },
  wrongTickContainer: {
    height: 22,
    width: 22,
    borderWidth: 1,
    borderColor: "rgba(0,0,0,0.2)",
    backgroundColor: "rgba(255, 46, 0, 1)",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 22,
    padding: 5,
  },
  answerText: {
    marginLeft: 10,
    paddingRight: 10,
  },
  rightAnswerContainer: {
    backgroundColor: "rgba(0, 171, 89, 0.05)",
    borderColor: "rgba(0, 171, 89, 0.2)",
  },
  wrongAnswerContainer: {
    backgroundColor: "rgba(255, 46, 0, 0.05)",
    borderColor: "rgba(255, 46, 0, 0.2)",
  },
  footerContainer: {
    backgroundColor: "rgba(0, 171, 89, 1)",
    padding: 15,
    position: "absolute",
    bottom: 0,
    width: "100%",
  },
  nextQuestionCta: {
    backgroundColor: "#fff",
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 11,
    paddingHorizontal: 20,
    borderRadius: 8,
    maxWidth: 175,
  },
  footerText: {
    color: "#fff",
    lineHeight: 22,
    fontSize: 16,
    marginRight: 8,
    marginBottom: 16,
    marginTop: 3,
  },
  footerImage: {
    width: 100,
    height: 100,
    position: "absolute",
    top: -100,
    left: 20,
  },
});
